import { useEffect, useRef, useState } from "react";
import type {
	ProfesionalCardProps,
	ProfessionalSummary,
} from "../../core/types/tsx/components/Profesional/ProfesionalCard.types";
import {
	BadgeCheck,
	//Bell,
	Calendar,
	Check,
	Eye,
	MapPin,
	Pencil,
	ShieldCheck,
	Star,
} from "lucide-react";
import { useAuthSessionContext } from "../routing/auth-session-store";
import { useUpdateUser } from "../../hooks/control";
import { getImageUri } from "../../core/utils/imageLoad";
import defaultAvatarUrl from "../../assets/default-avatar.svg";

const mockProfessional: ProfessionalSummary = {
	name: "Roberto Sánchez",
	tagline:
		"Carpintero profesional con más de 15 años de experiencia en muebles a medida y reparaciones.",
	since: "marzo de 2023",
	location: "Caracas, Venezuela",
	rating: 4.9,
	reviews: 428,
	avatar: "https://m.media-amazon.com/images/M/MV5BZDE2ZjIxYzUtZTJjYS00OWQ0LTk2NGEtMDliYmI3MzMwYjhkXkEyXkFqcGdeQWFsZWxvZw@@._V1_.jpg",
	verified: true,
	experience: "Carpinteros • 15 años de experiencia",
};

const MAX_BIO_CHARACTERS = 140;
const FALLBACK_AVATAR = defaultAvatarUrl;

const resolveAvatarUrl = (raw?: string) => {
	if (!raw) return FALLBACK_AVATAR;
	if (raw.length === 1) return raw;
	if (raw.startsWith("blob:")) return raw;
	return getImageUri(raw) ?? raw;
};

const truncateText = (value: string, maxLength: number) => {
	const normalized = value.trim();
	if (!normalized) return "";
	if (normalized.length <= maxLength) return normalized;
	return `${normalized.slice(0, maxLength).trimEnd()}...`;
};

const toMunicipioEstado = (rawLocation?: string) => {
	const normalized = (rawLocation ?? "").trim();
	if (!normalized) return "Ubicación no especificada";

	const parts = normalized
		.split(",")
		.map((part) => part.trim())
		.filter(Boolean);

	const municipio = parts.find((part) => /^municipio\b/i.test(part));
	const estado = parts.find((part) => /^estado\b/i.test(part));

	if (municipio && estado) {
		return `${municipio}, ${estado}`;
	}

	if (parts.length >= 2) {
		return `${parts[parts.length - 2]}, ${parts[parts.length - 1]}`;
	}

	return normalized;
};

export const ProfesionalCard = ({
	profile = mockProfessional,
	onPreview,
}: ProfesionalCardProps) => {
	const { session } = useAuthSessionContext();
	const userId = Number(session?.user?.id ?? 0);
	const { update, loading: updatingAvatar } = useUpdateUser(userId);
	const [avatarSrc, setAvatarSrc] = useState<string>(resolveAvatarUrl(profile.avatar));
	const [uploadState, setUploadState] = useState<
		"idle" | "uploading" | "success" | "success-fade"
	>("idle");
	const [uploadProgress, setUploadProgress] = useState(0);
	const fileInputRef = useRef<HTMLInputElement | null>(null);
	const previewUrlRef = useRef<string | null>(null);
	const progressIntervalRef = useRef<number | null>(null);
	const successFadeTimeoutRef = useRef<number | null>(null);
	const successClearTimeoutRef = useRef<number | null>(null);
	const progressRadius = 14;
	const progressCircumference = 2 * Math.PI * progressRadius;
	const progressOffset =
		progressCircumference - (uploadProgress / 100) * progressCircumference;

	useEffect(() => {
		const nextAvatar = resolveAvatarUrl(profile.avatar);
		if (!previewUrlRef.current) {
			setAvatarSrc(nextAvatar);
		}
	}, [profile.avatar]);

	useEffect(() => {
		return () => {
			if (previewUrlRef.current) {
				URL.revokeObjectURL(previewUrlRef.current);
			}
			if (progressIntervalRef.current) {
				window.clearInterval(progressIntervalRef.current);
			}
			if (successFadeTimeoutRef.current) {
				window.clearTimeout(successFadeTimeoutRef.current);
			}
			if (successClearTimeoutRef.current) {
				window.clearTimeout(successClearTimeoutRef.current);
			}
		};
	}, []);

	const onPickImage = async (event: React.ChangeEvent<HTMLInputElement>) => {
		const file = event.target.files?.[0];
		if (!file || !userId) return;

		const isImage = file.type.startsWith("image/");
		if (!isImage) {
			window.alert("Selecciona un archivo de imagen valido.");
			return;
		}

		if (file.size > 5 * 1024 * 1024) {
			window.alert("La imagen no puede superar los 5MB.");
			return;
		}

		if (previewUrlRef.current) {
			URL.revokeObjectURL(previewUrlRef.current);
		}

		const tempPreview = URL.createObjectURL(file);
		previewUrlRef.current = tempPreview;
		setAvatarSrc(tempPreview);

		if (progressIntervalRef.current) {
			window.clearInterval(progressIntervalRef.current);
			progressIntervalRef.current = null;
		}
		if (successFadeTimeoutRef.current) {
			window.clearTimeout(successFadeTimeoutRef.current);
			successFadeTimeoutRef.current = null;
		}
		if (successClearTimeoutRef.current) {
			window.clearTimeout(successClearTimeoutRef.current);
			successClearTimeoutRef.current = null;
		}
		setUploadState("uploading");
		setUploadProgress(0);
		progressIntervalRef.current = window.setInterval(() => {
			setUploadProgress((prev) => (prev >= 90 ? 90 : prev + 5));
		}, 120);

		try {
			const updated = await update({}, { image_profile: file });
			const persistedRaw =
				typeof updated?.image_profile === "string" ? updated.image_profile : null;
			const persistedUrl = resolveAvatarUrl(persistedRaw ?? undefined);

			if (persistedRaw) {
				URL.revokeObjectURL(tempPreview);
				previewUrlRef.current = null;
				setAvatarSrc(persistedUrl);
			}

			if (progressIntervalRef.current) {
				window.clearInterval(progressIntervalRef.current);
				progressIntervalRef.current = null;
			}
			setUploadProgress(100);
			setUploadState("success");
			successFadeTimeoutRef.current = window.setTimeout(() => {
				setUploadState("success-fade");
				successFadeTimeoutRef.current = null;
			}, 700);
			successClearTimeoutRef.current = window.setTimeout(() => {
				setUploadState("idle");
				setUploadProgress(0);
				successClearTimeoutRef.current = null;
			}, 1050);
		} catch {
			if (progressIntervalRef.current) {
				window.clearInterval(progressIntervalRef.current);
				progressIntervalRef.current = null;
			}
			if (successFadeTimeoutRef.current) {
				window.clearTimeout(successFadeTimeoutRef.current);
				successFadeTimeoutRef.current = null;
			}
			if (successClearTimeoutRef.current) {
				window.clearTimeout(successClearTimeoutRef.current);
				successClearTimeoutRef.current = null;
			}
			URL.revokeObjectURL(tempPreview);
			previewUrlRef.current = null;
			setAvatarSrc(resolveAvatarUrl(profile.avatar));
			setUploadState("idle");
			setUploadProgress(0);
			window.alert("No se pudo actualizar la foto de perfil.");
		} finally {
			if (event.target) {
				event.target.value = "";
			}
		}
	};

	const bioPreview = truncateText(profile.tagline ?? "", MAX_BIO_CHARACTERS);
	const locationPreview = toMunicipioEstado(profile.location);
	const showLetterAvatar = avatarSrc.length === 1;

	return (
		<div className='mb-6 mt-8 px-4 sm:px-6'>
			<div className='flex flex-col gap-5 rounded-2xl border border-gray-100 bg-white p-4 shadow-sm sm:p-5 md:flex-row md:items-start md:gap-6 md:p-6'>
				<div className='flex w-full flex-col items-center gap-3 sm:flex-row sm:items-start md:w-[42%] lg:w-[38%]'>
					<div className='flex flex-col items-center'>
						<div className='relative group h-20 w-20 shrink-0 overflow-hidden rounded-full shadow-sm sm:h-24 sm:w-24'>
							<input
								ref={fileInputRef}
								type='file'
								accept='image/*'
								onChange={onPickImage}
								className='hidden'
								aria-label='Cambiar foto de perfil'
							/>

							{showLetterAvatar ? (
								<div className='h-full w-full rounded-full bg-gray-200 flex items-center justify-center text-2xl md:text-3xl font-bold text-gray-500'>
									{avatarSrc}
								</div>
							) : (
								<img
									src={avatarSrc || FALLBACK_AVATAR}
									alt={profile.name}
									className='h-full w-full rounded-full object-cover'
								/>
							)}

							{uploadState !== "idle" ? (
								<div className='pointer-events-none absolute inset-0 z-10 flex items-center justify-center rounded-full bg-black/45'>
									{uploadState === "uploading" ? (
										<div className='relative h-10 w-10'>
											<svg
												viewBox='0 0 36 36'
												className='h-10 w-10 -rotate-90'
											>
												<circle
													cx='18'
													cy='18'
													r={progressRadius}
													stroke='rgba(255,255,255,0.35)'
													strokeWidth='3'
													fill='none'
												/>
												<circle
													cx='18'
													cy='18'
													r={progressRadius}
													stroke='white'
													strokeWidth='3'
													strokeLinecap='round'
													fill='none'
													strokeDasharray={
														progressCircumference
													}
													strokeDashoffset={progressOffset}
													className='transition-all duration-150'
												/>
											</svg>
											<span className='absolute inset-0 flex items-center justify-center text-[10px] font-semibold text-white'>
												{uploadProgress}%
											</span>
										</div>
									) : (
										<div
											className={`flex h-10 w-10 items-center justify-center rounded-full bg-green-500 text-white transition-opacity duration-300 ${
												uploadState === "success-fade"
													? "opacity-0"
													: "opacity-100"
											}`}
										>
											<Check className='h-5 w-5' />
										</div>
									)}
								</div>
							) : null}

							<button
								type='button'
								onClick={() => fileInputRef.current?.click()}
								disabled={
									updatingAvatar ||
									uploadState === "uploading" ||
									!userId
								}
								className='cursor-pointer absolute inset-0 flex items-center justify-center rounded-full bg-black/0 text-white opacity-0 transition-all group-hover:bg-black/35 group-hover:opacity-100 focus-visible:bg-black/35 focus-visible:opacity-100 disabled:cursor-not-allowed disabled:opacity-0'
								aria-label='Cambiar foto de perfil'
								title='Cambiar foto de perfil'
							>
								<Pencil className='h-5 w-5' />
							</button>
						</div>
					</div>
					<div className='min-w-0 w-full text-center sm:text-left'>
						<div className='flex min-w-0 flex-wrap items-center justify-center gap-2 sm:justify-start'>
							<h2 className='min-w-0 max-w-full truncate text-xl font-semibold leading-tight text-gray-800 sm:text-2xl'>
								{profile.name}
							</h2>
							{profile.verified ? (
								<span className='inline-flex shrink-0 items-center gap-1 px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 text-xs font-semibold'>
									<BadgeCheck className='w-4 h-4' /> Verificado
								</span>
							) : (
								<span className='inline-flex shrink-0 items-center gap-1 px-2.5 py-1 rounded-full bg-amber-50 text-amber-700 text-xs font-semibold'>
									<ShieldCheck className='w-4 h-4' /> En revisión
								</span>
							)}
						</div>
						<p className='mt-1 text-sm text-gray-500'>{profile.experience}</p>
						<p className='mt-1 inline-flex w-full items-center justify-center gap-2 text-xs text-gray-500 sm:justify-start'>
							<Calendar className='w-4 h-4 text-primary' /> Miembro desde{" "}
							<span className='truncate'>{profile.since}</span>
						</p>
					</div>
				</div>

				<div className='text-center text-sm leading-relaxed text-gray-600 md:flex-1 md:text-left'>
					{bioPreview || "Sin biografía profesional"}
					<div className='mt-2 flex flex-wrap justify-center gap-3 text-xs text-gray-500 md:justify-start'>
						<span className='inline-flex items-center gap-1'>
							<MapPin className='w-4 h-4' /> {locationPreview}
						</span>
					</div>
				</div>

				<div className='flex w-full flex-col items-center gap-3 sm:w-auto md:self-start'>
					<div className='text-center'>
						<p className='text-lg font-semibold text-gray-800 flex items-center gap-1 justify-center'>
							<Star className='w-4 h-4 text-[#894C00]' />{" "}
							{profile.rating.toFixed(1)}
						</p>
						<p className='text-xs text-gray-500'>
							{profile.reviews.toLocaleString()} reseñas
						</p>
					</div>
					{onPreview && (
						<button
							type='button'
							onClick={onPreview}
							className='cursor-pointer inline-flex items-center gap-1.5 rounded-lg border border-primary/30 bg-primary/5 px-3 py-1.5 text-xs font-medium text-primary transition hover:bg-primary/10'
						>
							<Eye className='h-3.5 w-3.5' />
							Vista como cliente
						</button>
					)}
				</div>
			</div>
		</div>
	);
};
